import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import AdminSidebar from '../components/AdminSidebar';
import PassagePicker from '../components/PassagePicker';
import DashboardHeader from '../components/DashboardHeader';
import './Dashboard.css';

/** Sentinel select values; must not match real DB strings. */
const GENRE_OTHER = '__genre_other__';
const STYLE_OTHER = '__style_other__';
const AUTHOR_OTHER = '__author_other__';
const BOOK_OTHER = '__book_other__';

function optionalPassagePick(pick, otherSentinel, newVal) {
  if (!pick) return undefined;
  if (pick === otherSentinel) return newVal.trim() || undefined;
  return pick.trim() || undefined;
}

function mapOptionalFieldToPick(value, suggestions, otherSentinel) {
  const trimmed = value != null ? String(value).trim() : '';
  if (!trimmed) return { pick: '', newVal: '' };
  if (suggestions.includes(trimmed)) return { pick: trimmed, newVal: '' };
  return { pick: otherSentinel, newVal: trimmed };
}

function passagePickFieldsFromPassage(p, { books, genres, styles }) {
  const genreTrim = (p.genre != null ? String(p.genre) : '').trim();
  let genrePick = '';
  let genreNew = '';
  if (genreTrim && genres.includes(genreTrim)) {
    genrePick = genreTrim;
  } else if (genreTrim) {
    genrePick = GENRE_OTHER;
    genreNew = genreTrim;
  }

  const b = mapOptionalFieldToPick(p.book, books, BOOK_OTHER);
  const s = mapOptionalFieldToPick(p.style, styles, STYLE_OTHER);

  return {
    genrePick,
    genreNew,
    bookPick: b.pick,
    bookNew: b.newVal,
    stylePick: s.pick,
    styleNew: s.newVal,
    dynastyId: p.dynastyId != null && p.dynastyId !== '' ? String(p.dynastyId) : '',
  };
}

function applyPassagePickFields(p, { books, genres, styles }, setters) {
  const fields = passagePickFieldsFromPassage(p, { books, genres, styles });
  setters.setGenrePick(fields.genrePick);
  setters.setGenreNew(fields.genreNew);
  setters.setBookPick(fields.bookPick);
  setters.setBookNew(fields.bookNew);
  setters.setStylePick(fields.stylePick);
  setters.setStyleNew(fields.styleNew);
  setters.setDynastyId(fields.dynastyId);
}

const Passage = () => {
  const navigate = useNavigate();
  const [dynasties, setDynasties] = useState([]);
  const [genres, setGenres] = useState([]);
  const [dynastiesLoading, setDynastiesLoading] = useState(true);
  const [dynastiesError, setDynastiesError] = useState('');
  const [genresError, setGenresError] = useState('');
  const [styles, setStyles] = useState([]);
  const [stylesError, setStylesError] = useState('');
  const [authors, setAuthors] = useState([]);
  const [authorsError, setAuthorsError] = useState('');
  const [books, setBooks] = useState([]);
  const [booksError, setBooksError] = useState('');
  const [title, setTitle] = useState('');
  const [authorPick, setAuthorPick] = useState('');
  const [authorNew, setAuthorNew] = useState('');
  const [bookPick, setBookPick] = useState('');
  const [bookNew, setBookNew] = useState('');
  const [genrePick, setGenrePick] = useState('');
  const [genreNew, setGenreNew] = useState('');
  const [dynastyId, setDynastyId] = useState('');
  const [stylePick, setStylePick] = useState('');
  const [styleNew, setStyleNew] = useState('');
  const [dse, setDse] = useState(false);
  const [taiwan30, setTaiwan30] = useState(false);
  const [taiwan15, setTaiwan15] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [passages, setPassages] = useState([]);
  const [passagesLoading, setPassagesLoading] = useState(true);
  const [passagesError, setPassagesError] = useState('');
  const [selectedPassageId, setSelectedPassageId] = useState('');
  const [templatePassage, setTemplatePassage] = useState(null);
  const [passageLoadLoading, setPassageLoadLoading] = useState(false);
  const [passageLoadError, setPassageLoadError] = useState('');
  const authorPrefetchSeqRef = useRef(0);

  const applyPickFields = useCallback(
    (p) => {
      applyPassagePickFields(p, { books, genres, styles }, {
        setGenrePick,
        setGenreNew,
        setBookPick,
        setBookNew,
        setStylePick,
        setStyleNew,
        setDynastyId,
      });
    },
    [books, genres, styles]
  );

  const clearInsertFields = useCallback(() => {
    setTitle('');
    setAuthorPick('');
    setAuthorNew('');
    setBookPick('');
    setBookNew('');
    setGenrePick('');
    setGenreNew('');
    setDynastyId('');
    setStylePick('');
    setStyleNew('');
    setDse(false);
    setTaiwan30(false);
    setTaiwan15(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setPassagesLoading(true);
      setPassagesError('');
      try {
        const res = await api.get('/data/passages?all=1');
        if (!cancelled) {
          setPassages(res.data.passages || []);
          setPassagesError('');
        }
      } catch (err) {
        if (!cancelled) {
          setPassages([]);
          setPassagesError(
            err.response?.data?.error ||
              err.response?.data?.details ||
              'Failed to load passages'
          );
        }
      } finally {
        if (!cancelled) setPassagesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectPassageById = useCallback(
    async (raw) => {
      setPassageLoadError('');
      const str = raw === '' || raw == null ? '' : String(raw);
      if (!str) {
        setSelectedPassageId('');
        setTemplatePassage(null);
        clearInsertFields();
        return;
      }
      const pid = parseInt(str, 10);
      if (Number.isNaN(pid)) return;
      setSelectedPassageId(pid);
      setPassageLoadLoading(true);
      try {
        const res = await api.get(`/data/passage?passageId=${pid}`);
        setTemplatePassage(res.data.passage || null);
      } catch (err) {
        setTemplatePassage(null);
        setPassageLoadError(
          err.response?.data?.error ||
            err.response?.data?.details ||
            'Failed to load passage'
        );
      } finally {
        setPassageLoadLoading(false);
      }
    },
    [clearInsertFields]
  );

  useEffect(() => {
    if (!templatePassage) return;
    const p = templatePassage;
    setTitle(typeof p.title === 'string' ? p.title : p.title != null ? String(p.title) : '');

    applyPickFields(p);

    const a = mapOptionalFieldToPick(p.author, authors, AUTHOR_OTHER);
    setAuthorPick(a.pick);
    setAuthorNew(a.newVal);

    setDse(Boolean(p.dse));
    setTaiwan30(Boolean(p.taiwan30));
    setTaiwan15(Boolean(p.taiwan15));
  }, [templatePassage, genres, authors, books, styles, applyPickFields]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setDynastiesLoading(true);
      setDynastiesError('');
      setGenresError('');
      setStylesError('');
      setAuthorsError('');
      setBooksError('');
      const [dRes, gRes, sRes, aRes, bRes] = await Promise.allSettled([
        api.get('/data/dynasties'),
        api.get('/data/passage-genres'),
        api.get('/data/passage-styles'),
        api.get('/data/passage-authors'),
        api.get('/data/passage-books'),
      ]);
      if (cancelled) return;
      if (dRes.status === 'fulfilled') {
        setDynasties(dRes.value.data.dynasties || []);
        setDynastiesError('');
      } else {
        const reason = dRes.reason;
        setDynastiesError(
          reason?.response?.data?.error ||
            reason?.response?.data?.details ||
            'Failed to load dynasties'
        );
        setDynasties([]);
      }
      if (gRes.status === 'fulfilled') {
        setGenres(gRes.value.data.genres || []);
        setGenresError('');
      } else {
        setGenres([]);
        const reason = gRes.reason;
        setGenresError(
          reason?.response?.data?.error ||
            reason?.response?.data?.details ||
            'Failed to load genre suggestions'
        );
      }
      if (sRes.status === 'fulfilled') {
        setStyles(sRes.value.data.styles || []);
        setStylesError('');
      } else {
        setStyles([]);
        const reason = sRes.reason;
        setStylesError(
          reason?.response?.data?.error ||
            reason?.response?.data?.details ||
            'Failed to load style suggestions'
        );
      }
      if (aRes.status === 'fulfilled') {
        setAuthors(aRes.value.data.authors || []);
        setAuthorsError('');
      } else {
        setAuthors([]);
        const reason = aRes.reason;
        setAuthorsError(
          reason?.response?.data?.error ||
            reason?.response?.data?.details ||
            'Failed to load author suggestions'
        );
      }
      if (bRes.status === 'fulfilled') {
        setBooks(bRes.value.data.books || []);
        setBooksError('');
      } else {
        setBooks([]);
        const reason = bRes.reason;
        setBooksError(
          reason?.response?.data?.error ||
            reason?.response?.data?.details ||
            'Failed to load book suggestions'
        );
      }
      setDynastiesLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const isEditingExisting = selectedPassageId !== '';

  const prefetchMetadataForAuthor = useCallback(
    async (author) => {
      const seq = ++authorPrefetchSeqRef.current;
      try {
        const res = await api.get('/data/passage', { params: { author } });
        if (seq !== authorPrefetchSeqRef.current) return;
        const passage = res.data?.passage;
        if (!passage) return;
        applyPickFields(passage);
      } catch {
        /* pre-fill is optional */
      }
    },
    [applyPickFields]
  );

  const handleAuthorChange = useCallback(
    (v) => {
      setAuthorPick(v);
      if (v !== AUTHOR_OTHER) setAuthorNew('');
      if (!v || v === AUTHOR_OTHER || selectedPassageId !== '') return;
      void prefetchMetadataForAuthor(v);
    },
    [prefetchMetadataForAuthor, selectedPassageId]
  );

  const performSavePassage = async () => {
    setMessage({ type: '', text: '' });
    setSubmitLoading(true);
    try {
      const genreValue =
        genrePick === GENRE_OTHER ? genreNew.trim() : genrePick.trim();
      if (!genrePick) {
        setMessage({ type: 'error', text: 'Please select a genre.' });
        return null;
      }
      if (genrePick === GENRE_OTHER && !genreNew.trim()) {
        setMessage({ type: 'error', text: 'Enter a new genre name.' });
        return null;
      }
      const styleValue = optionalPassagePick(stylePick, STYLE_OTHER, styleNew);
      const authorValue = optionalPassagePick(authorPick, AUTHOR_OTHER, authorNew);
      const bookValue = optionalPassagePick(bookPick, BOOK_OTHER, bookNew);
      const body = {
        title: title.trim(),
        genre: genreValue,
        dynastyId: parseInt(dynastyId, 10),
        author: authorValue,
        book: bookValue,
        style: styleValue,
        dse,
        taiwan30,
        taiwan15,
      };

      if (isEditingExisting) {
        const pid = Number(selectedPassageId);
        if (!Number.isFinite(pid)) {
          setMessage({ type: 'error', text: 'Invalid passage selection.' });
          return null;
        }
        const res = await api.put('/data/passage', { passageId: pid, ...body });
        const row = res.data?.data;
        const ord = row?.order;
        setMessage({
          type: 'success',
          text:
            row?.passage_id != null
              ? `${res.data?.message || 'Saved'} (passage_id: ${row.passage_id}${ord != null ? `, order: ${ord}` : ''})`
              : res.data?.message || 'Passage updated',
        });
        try {
          const ref = await api.get(`/data/passage?passageId=${pid}`);
          setTemplatePassage(ref.data.passage || null);
        } catch {
          /* keep previous template on refetch failure */
        }
        try {
          const listRes = await api.get('/data/passages?all=1');
          setPassages(listRes.data.passages || []);
        } catch {
          /* list refresh is optional */
        }
        setPassageLoadError('');
        return pid;
      }

      const res = await api.post('/data/passage', body);
      const row = res.data?.data;
      const pid = row?.passage_id;
      const ord = row?.order;
      setMessage({
        type: 'success',
        text:
          pid != null
            ? `${res.data?.message || 'Saved'} (passage_id: ${pid}${ord != null ? `, order: ${ord}` : ''})`
            : res.data?.message || 'Passage created',
      });
      clearInsertFields();
      setSelectedPassageId('');
      setTemplatePassage(null);
      setPassageLoadError('');
      if (pid == null) return null;
      const n = Number(pid);
      return Number.isFinite(n) ? n : null;
    } catch (err) {
      const msg =
        err.response?.data?.error ||
        err.response?.data?.details ||
        (isEditingExisting ? 'Failed to update passage' : 'Failed to insert passage');
      setMessage({ type: 'error', text: msg });
      return null;
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await performSavePassage();
  };

  const handleSaveAndGoParagraph = async () => {
    const pid = await performSavePassage();
    if (pid != null) navigate(`/paragraph?passageId=${pid}`);
  };

  const handleDeletePassage = async () => {
    if (!isEditingExisting) return;
    const pid = Number(selectedPassageId);
    if (!Number.isFinite(pid)) {
      setMessage({ type: 'error', text: 'Invalid passage selection.' });
      return;
    }
    if (!window.confirm(`Delete passage ${pid}? This cannot be undone.`)) return;

    setMessage({ type: '', text: '' });
    setDeleteLoading(true);
    try {
      await api.delete('/data/passage', { params: { passageId: pid } });
      clearInsertFields();
      setSelectedPassageId('');
      setTemplatePassage(null);
      setPassageLoadError('');
      setMessage({ type: 'success', text: `Passage ${pid} deleted.` });
      try {
        const listRes = await api.get('/data/passages?all=1');
        setPassages(listRes.data.passages || []);
      } catch {
        /* list refresh is optional */
      }
    } catch (err) {
      setMessage({
        type: 'error',
        text:
          err.response?.data?.error ||
          err.response?.data?.details ||
          'Failed to delete passage',
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const insertDisabledBase =
    submitLoading ||
    deleteLoading ||
    dynastiesLoading ||
    !!dynastiesError ||
    (!dynastiesLoading && dynasties.length === 0);

  return (
    <div className="dashboard">
      <DashboardHeader title="New passage" />

      <div className="dashboard-layout">
        <AdminSidebar />
        <div className="dashboard-content" style={{ maxWidth: 720, margin: '0 auto' }}>
        {message.text && <div className={`message ${message.type}`}>{message.text}</div>}

        <div className="form-section">
          <form onSubmit={handleSubmit} className="insert-form">
            <h2>Insert into passage</h2>

            <div className="form-group">
              <PassagePicker
                inputId="passage-picker-input"
                label="Find and load passage"
                passages={passages}
                passagesLoading={passagesLoading}
                passagesError={passagesError}
                value={selectedPassageId === '' ? '' : String(selectedPassageId)}
                onChange={(id) => {
                  void selectPassageById(id);
                }}
                extraDisabled={passageLoadLoading}
                titleFallback={
                  templatePassage?.title != null ? String(templatePassage.title) : ''
                }
              >
                {passageLoadLoading ? (
                  <div className="loading-state" style={{ marginTop: 8 }}>
                    Loading passage…
                  </div>
                ) : null}
                {passageLoadError ? (
                  <div className="message error" style={{ marginTop: 8 }}>
                    {passageLoadError}
                  </div>
                ) : null}
              </PassagePicker>
            </div>

            <div className="form-group">
              <label>
                Title <span className="required">*</span>
              </label>
              <textarea
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                required
                rows={2}
              />
            </div>

            <div className="form-group">
              <label htmlFor="passage-author-select">Author</label>
              <select
                id="passage-author-select"
                value={authorPick}
                onChange={(e) => handleAuthorChange(e.target.value)}
              >
                <option value="">— None —</option>
                {authors.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
                <option value={AUTHOR_OTHER}>Other… (type new author)</option>
              </select>
              {authorsError ? (
                <div className="message error" style={{ marginTop: 8 }}>
                  {authorsError}
                </div>
              ) : null}
              {!authorsError && authors.length === 0 ? (
                <small className="form-hint" style={{ display: 'block', marginTop: 8 }}>
                  No saved authors found yet — use &quot;Other…&quot; or leave as None.
                </small>
              ) : null}
              {authorPick === AUTHOR_OTHER && (
                <div style={{ marginTop: 12 }}>
                  <label htmlFor="passage-author-new">New author</label>
                  <input
                    id="passage-author-new"
                    type="text"
                    value={authorNew}
                    onChange={(e) => setAuthorNew(e.target.value)}
                    autoComplete="off"
                    placeholder="Author name"
                  />
                </div>
              )}
              <small className="form-hint">
                Optional. Pick an existing author, &quot;Other…&quot; for a new one, or None.
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="passage-book-select">Book</label>
              <select
                id="passage-book-select"
                value={bookPick}
                onChange={(e) => {
                  const v = e.target.value;
                  setBookPick(v);
                  if (v !== BOOK_OTHER) setBookNew('');
                }}
              >
                <option value="">— None —</option>
                {books.map((bk) => (
                  <option key={bk} value={bk}>
                    {bk}
                  </option>
                ))}
                <option value={BOOK_OTHER}>Other… (type new book)</option>
              </select>
              {booksError ? (
                <div className="message error" style={{ marginTop: 8 }}>
                  {booksError}
                </div>
              ) : null}
              {!booksError && books.length === 0 ? (
                <small className="form-hint" style={{ display: 'block', marginTop: 8 }}>
                  No saved books found yet — use &quot;Other…&quot; or leave as None.
                </small>
              ) : null}
              {bookPick === BOOK_OTHER && (
                <div style={{ marginTop: 12 }}>
                  <label htmlFor="passage-book-new">New book</label>
                  <input
                    id="passage-book-new"
                    type="text"
                    value={bookNew}
                    onChange={(e) => setBookNew(e.target.value)}
                    autoComplete="off"
                    placeholder="Book title"
                  />
                </div>
              )}
              <small className="form-hint">
                Optional. Pick an existing book, &quot;Other…&quot; for a new one, or None.
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="passage-genre-select">
                Genre <span className="required">*</span>
              </label>
              <select
                id="passage-genre-select"
                value={genrePick}
                onChange={(e) => {
                  const v = e.target.value;
                  setGenrePick(v);
                  if (v !== GENRE_OTHER) setGenreNew('');
                }}
                required
              >
                <option value="">— Select genre —</option>
                {genres.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
                <option value={GENRE_OTHER}>Other… (type new genre)</option>
              </select>
              {genresError ? (
                <div className="message error" style={{ marginTop: 8 }}>
                  {genresError}
                </div>
              ) : null}
              {!genresError && genres.length === 0 ? (
                <small className="form-hint" style={{ display: 'block', marginTop: 8 }}>
                  No saved genres found yet — use &quot;Other…&quot; to add one.
                </small>
              ) : null}
              {genrePick === GENRE_OTHER && (
                <div style={{ marginTop: 12 }}>
                  <label htmlFor="passage-genre-new">New genre name</label>
                  <input
                    id="passage-genre-new"
                    type="text"
                    value={genreNew}
                    onChange={(e) => setGenreNew(e.target.value)}
                    required
                    autoComplete="off"
                    placeholder="e.g. 議論文"
                  />
                </div>
              )}
              <small className="form-hint">
                Pick an existing genre from the list, or choose &quot;Other…&quot; to add a new one.
              </small>
            </div>

            <div className="form-group">
              <label>
                Dynasty <span className="required">*</span>
              </label>
              {dynastiesLoading ? (
                <div className="loading-state">Loading dynasties...</div>
              ) : dynastiesError ? (
                <div className="message error">{dynastiesError}</div>
              ) : (
                <select
                  value={dynastyId}
                  onChange={(e) => setDynastyId(e.target.value)}
                  required
                >
                  <option value="">— Select dynasty —</option>
                  {dynasties.map((d) => (
                    <option key={d.dynasty_id} value={String(d.dynasty_id)}>
                      {d.value != null && d.value !== '' ? d.value : `ID ${d.dynasty_id}`}
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="passage-style-select">Style</label>
              <select
                id="passage-style-select"
                value={stylePick}
                onChange={(e) => {
                  const v = e.target.value;
                  setStylePick(v);
                  if (v !== STYLE_OTHER) setStyleNew('');
                }}
              >
                <option value="">— None —</option>
                {styles.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
                <option value={STYLE_OTHER}>Other… (type new style)</option>
              </select>
              {stylesError ? (
                <div className="message error" style={{ marginTop: 8 }}>
                  {stylesError}
                </div>
              ) : null}
              {!stylesError && styles.length === 0 ? (
                <small className="form-hint" style={{ display: 'block', marginTop: 8 }}>
                  No saved styles found yet — use &quot;Other…&quot; or leave as None.
                </small>
              ) : null}
              {stylePick === STYLE_OTHER && (
                <div style={{ marginTop: 12 }}>
                  <label htmlFor="passage-style-new">New style</label>
                  <input
                    id="passage-style-new"
                    type="text"
                    value={styleNew}
                    onChange={(e) => setStyleNew(e.target.value)}
                    autoComplete="off"
                    placeholder="e.g. 文言文"
                  />
                </div>
              )}
              <small className="form-hint">
                Optional. Pick an existing style, &quot;Other…&quot; for a new one, or None.
              </small>
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={dse}
                  onChange={(e) => setDse(e.target.checked)}
                />{' '}
                DSE
              </label>
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={taiwan30}
                  onChange={(e) => setTaiwan30(e.target.checked)}
                />{' '}
                Taiwan 30
              </label>
            </div>
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={taiwan15}
                  onChange={(e) => setTaiwan15(e.target.checked)}
                />{' '}
                Taiwan 15
              </label>
            </div>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 20 }}>
              <button
                type="submit"
                className="submit-button"
                style={{ marginTop: 0 }}
                disabled={insertDisabledBase}
              >
                {submitLoading
                  ? isEditingExisting
                    ? 'Saving…'
                    : 'Inserting...'
                  : isEditingExisting
                    ? 'Save passage'
                    : 'Insert passage'}
              </button>
              <button
                type="button"
                className="submit-button"
                style={{ marginTop: 0 }}
                disabled={insertDisabledBase}
                onClick={handleSaveAndGoParagraph}
              >
                {submitLoading
                  ? isEditingExisting
                    ? 'Saving…'
                    : 'Inserting...'
                  : isEditingExisting
                    ? 'Save passage and go to Paragraph'
                    : 'Insert passage and go to Paragraph'}
              </button>
              {isEditingExisting && (
                <button
                  type="button"
                  className="submit-button"
                  style={{ marginTop: 0, backgroundColor: '#c0392b' }}
                  disabled={insertDisabledBase}
                  onClick={handleDeletePassage}
                >
                  {deleteLoading ? 'Deleting…' : 'Delete passage'}
                </button>
              )}
            </div>
          </form>
        </div>
        </div>
      </div>
    </div>
  );
};

export default Passage;
